import { z } from 'zod'

import { getFavoriteListIdsByProductIds } from '@/gen/bff'
import { zFavoriteListIdsResponseDto } from '@/gen/bff/zod.gen'
import { createBatchLoader } from '@/lib/batching/createBatchLoader'
import { createScopedLoaderCache } from '@/lib/batching/createScopedLoader'
import { defaultDataConverter } from '@/lib/converters/defaultDataConverter'
import { defaultZodCatch } from '@/lib/converters/defaultZodCatch'
import { getAllFavoriteListIdsByProductsConverter } from '@/lib/converters/favorite/getAllFavoriteListIdsByProducts/getAllFavoriteListIdsByProductsConverter'

import type { BatchLoader, BatchLoaderConfig } from '@/lib/batching/createBatchLoader'
import type { AllFavoriteListIdsByProducts } from '@/schemas/allFavoriteListIdsByProductsSchema'

export type FavoriteListIdsLoader = BatchLoader<string, string[]>

export interface FavoriteListIdsLoaderConfig
  extends Pick<BatchLoaderConfig<string, string[]>, 'maxBatchSize' | 'batchWindowMs'> {
  fetchFavoriteListIds: (productIds: string[]) => Promise<AllFavoriteListIdsByProducts[]>
}

export const createFavoriteListIdsLoader = ({
  fetchFavoriteListIds,
  maxBatchSize,
  batchWindowMs,
}: FavoriteListIdsLoaderConfig): FavoriteListIdsLoader =>
  createBatchLoader<string, string[]>({
    identify: (productId) => productId,
    missing: () => [],
    maxBatchSize,
    batchWindowMs,
    fetchChunk: async (productIds) => {
      const rows = await fetchFavoriteListIds(productIds)

      return new Map(rows.map((row) => [row.productIds, row.favoriteListIds]))
    },
  })

const parseResponse = (response: Awaited<ReturnType<typeof getFavoriteListIdsByProductIds>>) =>
  getAllFavoriteListIdsByProductsConverter(
    z.array(zFavoriteListIdsResponseDto).catch(defaultZodCatch).parse(defaultDataConverter(response))
  )

// One loader per customer+organisation: a soft org switch (no reload — see
// .claude/memory/project/commerce-flows/org-switch-soft-reload.md) can leave a load in flight when the org changes,
// so a shared loader could resolve it against the wrong organisation's batch.
export const getFavoriteListIdsLoader = createScopedLoaderCache<
  [customerId: string, orgUnitId: string],
  string,
  string[]
>(
  (customerId, orgUnitId) => `${customerId}::${orgUnitId}`,
  (customerId, orgUnitId) =>
    createFavoriteListIdsLoader({
      fetchFavoriteListIds: async (productIds) =>
        parseResponse(
          await getFavoriteListIdsByProductIds({
            body: { productIds },
            headers: {
              'X-CustomerId': customerId,
              'X-OrgUnit': orgUnitId,
            },
          })
        ),
    })
)
